import { create } from "zustand";
import type { GameMap } from "../api/types";
import { project, unproject } from "../lib/view";

/**
 * How the map is being looked at: how close, from which way up, and over what.
 *
 * The minimap is square, but the room it is given is not, so it is drawn as a
 * square centred in a canvas that fills whatever space there is. The camera is
 * a second transform applied after that projection, about the centre of the
 * canvas, so nothing else has to know it exists: world positions go in and
 * canvas pixels come out, zoomed, turned and shifted. Zooming therefore fills
 * the width rather than being cut off at the edge of a square.
 *
 * Icons and labels are deliberately not part of it. A zone doubles in size when
 * you zoom in, because it is ground; an agent portrait does not, because it is
 * a label.
 */

/** The canvas, and the square the map itself occupies inside it. */
export interface Viewport {
  width: number;
  height: number;
  /** Side of the centred square the minimap is drawn at. */
  map: number;
}
export interface Camera {
  zoom: number;
  /** Canvas pixels, applied after the zoom. */
  panX: number;
  panY: number;
  /** Always a multiple of 45: the map turns in eighths, not freely. */
  angle: number;
}

// Below one so the whole map still fits once it is turned: a square rotated by
// 45 degrees needs about seven tenths of the room it had.
export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 6;
export const TURN = 45;

const HOME: Camera = { zoom: 1, panX: 0, panY: 0, angle: 0 };

interface State extends Camera {
  zoomAt: (factor: number, at: [number, number], view: Viewport) => void;
  zoomBy: (factor: number) => void;
  pan: (dx: number, dy: number) => void;
  turn: (steps: number) => void;
  reset: () => void;
}

export const useCamera = create<State>((set) => ({
  ...HOME,

  /** Zooms about a point in canvas pixels, keeping the ground under it still. */
  zoomAt: (factor, [x, y], view) =>
    set((c) => {
      const zoom = clamp(c.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      if (zoom === c.zoom) return c;
      // Where that pixel sits in the untransformed projection, then where the
      // new zoom would put it, and the difference is the shift to undo.
      const scene = toScene([x, y], c, view);
      const [px, py] = fromScene(scene, { ...c, zoom, panX: 0, panY: 0 }, view);
      return { ...c, zoom, panX: x - px, panY: y - py };
    }),

  /** Zooms about the middle of the view, which is what a button means. The pan
   *  scales with the zoom, so the ground in the middle stays in the middle. */
  zoomBy: (factor) =>
    set((c) => {
      const zoom = clamp(c.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      const k = zoom / c.zoom;
      return { ...c, zoom, panX: c.panX * k, panY: c.panY * k };
    }),

  pan: (dx, dy) => set((c) => ({ ...c, panX: c.panX + dx, panY: c.panY + dy })),

  turn: (steps) =>
    set((c) => ({ ...c, angle: (((c.angle + steps * TURN) % 360) + 360) % 360 })),

  reset: () => set(HOME),
}));

/** Canvas pixels for a world position, with the camera applied. */
export function toScreen(
  map: GameMap,
  x: number,
  y: number,
  view: Viewport,
  camera: Camera,
): [number, number] {
  const [px, py] = project(map, x, y, view.map, view.map);
  const scene: [number, number] = [
    px + (view.width - view.map) / 2,
    py + (view.height - view.map) / 2,
  ];
  return fromScene(scene, camera, view);
}

/** The world position under a canvas pixel, with the camera undone. */
export function toWorld(
  map: GameMap,
  x: number,
  y: number,
  view: Viewport,
  camera: Camera,
): [number, number] {
  const [sx, sy] = toScene([x, y], camera, view);
  return unproject(
    map,
    sx - (view.width - view.map) / 2,
    sy - (view.height - view.map) / 2,
    view.map,
    view.map,
  );
}

/** Scene pixels, meaning the projection before the camera, to canvas pixels. */
function fromScene([sx, sy]: [number, number], c: Camera, view: Viewport): [number, number] {
  const cx = view.width / 2;
  const cy = view.height / 2;
  const radians = (c.angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = (sx - cx) * c.zoom;
  const dy = (sy - cy) * c.zoom;
  return [cx + dx * cos - dy * sin + c.panX, cy + dx * sin + dy * cos + c.panY];
}

/** Canvas pixels back to scene pixels. */
function toScene([x, y]: [number, number], c: Camera, view: Viewport): [number, number] {
  const cx = view.width / 2;
  const cy = view.height / 2;
  const radians = (c.angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = x - cx - c.panX;
  const dy = y - cy - c.panY;
  return [cx + (dx * cos + dy * sin) / c.zoom, cy + (-dx * sin + dy * cos) / c.zoom];
}

function clamp(n: number, low: number, high: number): number {
  return n < low ? low : n > high ? high : n;
}
